import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { KTSVG } from 'src/_metronic/helpers';
import { useAppSelector } from 'src/services/redux/app/store';
import { fetchQuickStockInfo, useQuickProductSearch, useStockReservation } from '../service';

interface Props {
  show: boolean;
  onClose: () => void;
}

type Step = 'form' | 'confirming' | 'success' | 'error';

const QuickReservationModal: React.FC<Props> = ({ show, onClose }) => {
  const { user } = useAppSelector((state: any) => state.auth);
  const [step, setStep] = useState<Step>('form');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [note, setNote] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resultData, setResultData] = useState<any>(null);
  const [stockInfoCache, setStockInfoCache] = useState<Record<string, { stock_quantity: number | null; available_quantity: number | null; reserved_quantity: number }>>({});
  const [stockInfoLoading, setStockInfoLoading] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const { data: searchResults, isLoading: searchLoading } = useQuickProductSearch(debouncedSearch);

  // Arama sonuçları geldiğinde stok bilgisini çek
  useEffect(() => {
    const products = Array.isArray(searchResults) ? searchResults : [];
    if (products.length === 0) return;
    const codes = products.map((p: any) => p.stock_code).filter((c: string) => !stockInfoCache[c]);
    if (codes.length === 0) return;
    setStockInfoLoading(true);
    fetchQuickStockInfo(codes)
      .then((info) => setStockInfoCache((prev) => ({ ...prev, ...info })))
      .catch(() => {})
      .finally(() => setStockInfoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  const { mutate: reservationMutate } = useStockReservation(
    (response: any) => {
      setResultData(response.data);
      setStep('success');
    },
    (error: any) => {
      setErrorMsg(error?.response?.data?.message || 'Rezervasyon oluşturulamadı');
      setStep('error');
    },
  );

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProduct = useCallback(
    (product: any) => {
      const info = stockInfoCache[product.stock_code];
      setSelectedProduct({ ...product, ...(info || {}) });
      setSearchText(product.stock_code);
      setShowDropdown(false);
      // Miktar, mevcut stoktan fazlaysa düşür
      const available = info?.available_quantity ?? info?.stock_quantity;
      if (available != null && available > 0 && quantity > available) {
        setQuantity(Math.floor(available));
      }
    },
    [stockInfoCache, quantity],
  );

  const handleSubmit = () => {
    if (!selectedProduct) {
      toast.warning('Lütfen bir ürün seçiniz');
      return;
    }
    if (quantity <= 0) {
      toast.warning('Miktar 0 dan büyük olmalıdır');
      return;
    }
    const availableQty = selectedProduct?.available_quantity ?? selectedProduct?.stock_quantity;
    if (availableQty != null && quantity > availableQty) {
      toast.warning(`Stok adedi yetersiz! Mevcut: ${availableQty}`);
      return;
    }
    if (!validUntil) {
      toast.warning('Lütfen bitiş tarihi seçiniz');
      return;
    }

    setStep('confirming');
    const userName = `${user?.name || ''} ${user?.surname || ''}`.trim();
    const finalNote = note ? (userName ? `${note} - ${userName}` : note) : userName || '';
    reservationMutate({
      stock_code: selectedProduct.stock_code,
      quantity,
      valid_until: validUntil,
      note: finalNote,
    });
  };

  const handleClose = () => {
    setStep('form');
    setSearchText('');
    setDebouncedSearch('');
    setSelectedProduct(null);
    setQuantity(1);
    setNote('');
    setErrorMsg('');
    setResultData(null);
    setStockInfoCache({});
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setValidUntil(d.toISOString().split('T')[0]);
    onClose();
  };

  if (!show) return null;

  const products = Array.isArray(searchResults) ? searchResults : [];

  return (
    <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: 900 }}>
        <div className="modal-content" style={{ borderRadius: 16, overflow: 'hidden' }}>
          {/* Header */}
          <div className="modal-header border-0 px-8 pt-6 pb-0">
            <div>
              <h4 className="modal-title fw-bolder d-flex align-items-center mb-1">
                <KTSVG path="/media/icons/duotone/General/Lock.svg" className="svg-icon-1 text-warning me-3" />
                Ürün Rezervasyonu
              </h4>
              <div className="fs-7 text-muted">DIA ERP üzerinde hızlı stok rezervasyonu oluşturun</div>
            </div>
            <button type="button" className="btn btn-sm btn-icon btn-active-light-primary" onClick={handleClose} disabled={step === 'confirming'}>
              <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1" />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body px-8 py-6">
            {/* Form Step */}
            {step === 'form' && (
              <>
                {/* Product Search */}
                <div className="mb-6" ref={dropdownRef}>
                  <label className="form-label fw-semibold fs-6 required">Ürün Seçimi</label>
                  <div className="position-relative">
                    <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-2 svg-icon-gray-500 position-absolute top-50 translate-middle-y ms-4" />
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control form-control-solid ps-13"
                      placeholder="Stok kodu veya ürün adı yazın..."
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setSelectedProduct(null);
                        setShowDropdown(true);
                      }}
                      onFocus={() => {
                        if (debouncedSearch.length >= 2) setShowDropdown(true);
                      }}
                    />
                    {searchLoading && (
                      <div className="position-absolute top-50 translate-middle-y end-0 me-4">
                        <span className="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                    )}
                  </div>

                  {/* Dropdown */}
                  {showDropdown && debouncedSearch.length >= 2 && (
                    <div
                      className="position-absolute bg-white shadow-lg rounded mt-1 overflow-auto"
                      style={{ maxHeight: 250, zIndex: 1060, width: 'calc(100% - 3rem)', left: '1.5rem' }}
                    >
                      {searchLoading ? (
                        <div className="text-center py-5">
                          <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                          <span className="text-muted fs-7">Aranıyor...</span>
                        </div>
                      ) : products.length > 0 ? (
                        products.map((p: any, idx: number) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center px-4 py-3 cursor-pointer hover-bg-light border-bottom"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectProduct(p)}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fa')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                          >
                            <div className="flex-grow-1">
                              <div className="fw-bold text-gray-800 fs-7">{p.stock_code}</div>
                              <div className="text-muted fs-8">{p.stock_name}</div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {stockInfoCache[p.stock_code]?.stock_quantity != null ? (
                                <span
                                  className={`badge ${
                                    (stockInfoCache[p.stock_code]?.available_quantity ?? stockInfoCache[p.stock_code]?.stock_quantity ?? 0) > 0
                                      ? 'badge-light-success'
                                      : 'badge-light-danger'
                                  } fs-9`}
                                >
                                  <i className="fas fa-boxes fs-9 me-1"></i>
                                  {stockInfoCache[p.stock_code]?.available_quantity ?? stockInfoCache[p.stock_code]?.stock_quantity} adet
                                  {(stockInfoCache[p.stock_code]?.reserved_quantity ?? 0) > 0 && (
                                    <span className="ms-1">
                                      ({stockInfoCache[p.stock_code]?.stock_quantity}-{stockInfoCache[p.stock_code]?.reserved_quantity})
                                    </span>
                                  )}
                                </span>
                              ) : stockInfoLoading ? (
                                <span className="spinner-border spinner-border-sm text-muted" style={{ width: 14, height: 14 }}></span>
                              ) : null}
                              {p.brand && <span className="badge badge-light-primary fs-9">{p.brand}</span>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-5 text-muted fs-7">Sonuç bulunamadı</div>
                      )}
                    </div>
                  )}

                  {/* Selected product badge */}
                  {selectedProduct && (
                    <div className="d-flex align-items-center mt-3 p-3 rounded" style={{ background: '#e8fff3', border: '1px solid #50cd89' }}>
                      <KTSVG path="/media/icons/duotone/Navigation/Check.svg" className="svg-icon-4 text-success me-2" />
                      <div className="flex-grow-1">
                        <span className="fw-bold text-gray-800 fs-7">{selectedProduct.stock_code}</span>
                        <span className="text-muted ms-2 fs-8">{selectedProduct.stock_name}</span>
                        {selectedProduct.stock_quantity != null && (
                          <span
                            className={`badge ms-2 ${
                              (selectedProduct.available_quantity ?? selectedProduct.stock_quantity ?? 0) > 0 ? 'badge-light-success' : 'badge-light-danger'
                            } fs-9`}
                          >
                            Stok: {selectedProduct.available_quantity ?? selectedProduct.stock_quantity}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-icon btn-active-light-danger"
                        onClick={() => {
                          setSelectedProduct(null);
                          setSearchText('');
                          inputRef.current?.focus();
                        }}
                      >
                        <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Quantity & Date Row */}
                <div className="row g-5 mb-6">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold fs-6 required">Miktar</label>
                    <input
                      type="number"
                      className={`form-control form-control-solid ${
                        selectedProduct?.available_quantity != null && quantity > (selectedProduct.available_quantity ?? selectedProduct.stock_quantity ?? Infinity)
                          ? 'border-danger'
                          : ''
                      }`}
                      value={quantity}
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value) || 1);
                        const maxQty = selectedProduct?.available_quantity ?? selectedProduct?.stock_quantity;
                        if (maxQty != null && val > maxQty) {
                          toast.warning(`Maksimum ${maxQty} adet rezerve edilebilir`);
                          setQuantity(Math.floor(maxQty));
                        } else {
                          setQuantity(val);
                        }
                      }}
                      min={1}
                      max={selectedProduct?.available_quantity ?? selectedProduct?.stock_quantity ?? undefined}
                    />
                    {selectedProduct?.available_quantity != null && (
                      <div className="form-text text-muted fs-9">Mevcut stok: {selectedProduct.available_quantity ?? selectedProduct.stock_quantity}</div>
                    )}
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-semibold fs-6 d-flex align-items-center required">
                      <KTSVG path="/media/icons/duotone/Home/Timer.svg" className="svg-icon-5 me-2 text-gray-400" />
                      Rezervasyon Bitiş Tarihi
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-solid"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                {/* Note */}
                <div className="mb-3">
                  <label className="form-label fw-semibold fs-6">Not</label>
                  <textarea
                    className="form-control form-control-solid"
                    rows={2}
                    maxLength={200}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="DIA'da görünecek rezervasyon notu..."
                  />
                  <div className="form-text text-end">{note.length}/200</div>
                </div>
              </>
            )}

            {/* Confirming Step */}
            {step === 'confirming' && (
              <div className="text-center py-15">
                <div className="spinner-border text-warning mb-5" style={{ width: '3.5rem', height: '3.5rem' }}></div>
                <div className="fw-bold fs-4 text-gray-700">DIA'da rezervasyon oluşturuluyor...</div>
                <div className="text-muted fs-6 mt-3">
                  <strong>{selectedProduct?.stock_code}</strong> — {quantity} adet
                </div>
              </div>
            )}

            {/* Success Step */}
            {step === 'success' && (
              <div className="text-center py-15">
                <div className="position-relative d-inline-block mb-7">
                  <div
                    className="position-absolute rounded-circle"
                    style={{
                      width: 120,
                      height: 120,
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      border: '3px solid #50cd89',
                      animation: 'qrSuccessRipple 1.5s ease-out infinite',
                      opacity: 0,
                    }}
                  />
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 90,
                      height: 90,
                      background: 'linear-gradient(135deg, #50cd89 0%, #0bb783 100%)',
                      animation: 'qrSuccessPopIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
                      boxShadow: '0 8px 30px rgba(80,205,137,0.4)',
                    }}
                  >
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ strokeDasharray: 30, strokeDashoffset: 30, animation: 'qrCheckStroke 0.6s ease 0.4s forwards' }}
                      />
                    </svg>
                  </div>
                </div>

                <div className="fw-bolder fs-2 text-gray-800 mb-3" style={{ animation: 'qrFadeUp 0.5s ease 0.5s both' }}>
                  Rezervasyon Başarılı!
                </div>
                <div className="text-muted fs-5 mb-5" style={{ animation: 'qrFadeUp 0.5s ease 0.7s both' }}>
                  {resultData?.message}
                </div>

                <div style={{ animation: 'qrFadeUp 0.5s ease 0.9s both' }}>
                  <div className="d-flex justify-content-center gap-4 mt-5">
                    <div className="border border-gray-300 rounded px-4 py-3 text-center" style={{ minWidth: 120 }}>
                      <div className="text-muted fs-8 fw-bold">Stok Kodu</div>
                      <div className="fw-bold text-gray-800 mt-1">{resultData?.stock_code}</div>
                    </div>
                    <div className="border border-gray-300 rounded px-4 py-3 text-center" style={{ minWidth: 80 }}>
                      <div className="text-muted fs-8 fw-bold">Miktar</div>
                      <div className="fw-bold text-gray-800 mt-1">{resultData?.quantity}</div>
                    </div>
                    <div className="border border-gray-300 rounded px-4 py-3 text-center" style={{ minWidth: 100 }}>
                      <div className="text-muted fs-8 fw-bold">Bitiş</div>
                      <div className="fw-bold text-gray-800 mt-1">{resultData?.valid_until}</div>
                    </div>
                  </div>
                </div>

                <style>{`
                  @keyframes qrSuccessPopIn {
                    0% { transform: scale(0); opacity: 0; }
                    60% { transform: scale(1.15); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                  }
                  @keyframes qrCheckStroke { to { stroke-dashoffset: 0; } }
                  @keyframes qrSuccessRipple {
                    0% { transform: translate(-50%,-50%) scale(0.7); opacity: 0.6; }
                    100% { transform: translate(-50%,-50%) scale(1.8); opacity: 0; }
                  }
                  @keyframes qrFadeUp {
                    0% { opacity: 0; transform: translateY(16px); }
                    100% { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
            )}

            {/* Error Step */}
            {step === 'error' && (
              <div className="text-center py-10">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-5"
                  style={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #f1416c 0%, #d9214e 100%)',
                    boxShadow: '0 8px 30px rgba(241,65,108,0.3)',
                  }}
                >
                  <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1 text-white svg-icon-white" />
                </div>
                <div className="fw-bolder fs-3 text-gray-800 mb-3">Rezervasyon Başarısız</div>
                {(() => {
                  const raw = errorMsg.replace(/<[^>]*>/g, '').trim();
                  const cleaned = raw.replace(/^DIA rezervasyon hatası:\s*/i, '');
                  const stockMatch = cleaned.match(/:\s*([A-Z0-9/]+)-(.+)$/);
                  const mainParts = cleaned.split(':');
                  const errorType = mainParts[0]?.includes('Eksi Stok') ? 'Yetersiz Stok' : mainParts[0]?.trim();
                  const errorDetail = stockMatch
                    ? cleaned
                        .substring(0, cleaned.indexOf(stockMatch[0]) - 2)
                        .replace(/^\*\s*/, '')
                        .trim()
                    : mainParts.length > 1
                    ? mainParts
                        .slice(0, -1)
                        .join(':')
                        .replace(/^\*\s*/, '')
                        .trim()
                    : cleaned;

                  return (
                    <div className="text-start mx-auto" style={{ maxWidth: 500 }}>
                      <div className="alert d-flex align-items-start p-5 mb-4" style={{ borderRadius: 12, background: '#fff5f8', border: '1px solid #f1416c33' }}>
                        <KTSVG path="/media/icons/duotone/Code/Warning-2.svg" className="svg-icon-2 text-danger me-4 mt-1 flex-shrink-0" />
                        <div>
                          {errorType && <span className="badge badge-light-danger fs-8 mb-2 d-inline-block">{errorType}</span>}
                          <div className="fs-6 text-gray-700 fw-semibold" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {errorDetail || cleaned}
                          </div>
                        </div>
                      </div>
                      {stockMatch && (
                        <div className="border border-gray-300 rounded p-4" style={{ borderRadius: 12 }}>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <div className="text-muted fs-8 fw-bold mb-1">Ürün</div>
                              <div className="fw-bold text-gray-800 fs-7">{stockMatch[1]}</div>
                              <div className="text-muted fs-8 mt-1">{stockMatch[2]?.trim()}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 px-8 pb-6 pt-2">
            {step === 'form' && (
              <button className="btn btn-warning btn-lg d-flex align-items-center px-6" onClick={handleSubmit} disabled={!selectedProduct || quantity <= 0 || !validUntil}>
                <KTSVG path="/media/icons/duotone/General/Lock.svg" className="svg-icon-3 me-2" />
                Rezervasyonu Oluştur
              </button>
            )}

            {step === 'error' && (
              <button className="btn btn-light-primary" onClick={() => setStep('form')}>
                Tekrar Dene
              </button>
            )}

            <button className="btn btn-light" onClick={handleClose} disabled={step === 'confirming'}>
              {step === 'success' || step === 'error' ? 'Kapat' : 'İptal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickReservationModal;
